/**
 * Rule failures. An op throws RuleError when its preconditions do not hold (the original UI enforced them by hiding
 * the button, new_interf.render) or when its arguments are invalid. The backend reports it as
 * VALIDATION_FAILED { reason, details }; the client re-adopts the server state and shows a short message.
 *
 * Reasons are stable machine codes (snake_case). Reuse an existing one when it fits; add new ones to RULE_REASONS.
 */

/** Well-known reason codes (ops may add their own; keep them snake_case and stable). */
export const RULE_REASONS = [
  /** The op name is not registered. */
  'unknown_op',
  /** Arguments are missing, of the wrong type, out of range, or contain unknown fields. */
  'bad_args',
  /** The stored game state cannot be decoded. */
  'bad_state',
  'not_enough_gold',
  'not_enough_diam',
  'level_too_low',
  'limit_reached',
  'not_owned',
  'unknown_item',
  'not_available',
] as const;

export type KnownRuleReason = (typeof RULE_REASONS)[number];
/** A known reason or an op-specific one. */
export type RuleReason = KnownRuleReason | (string & {});

export class RuleError extends Error {
  constructor(
    readonly reason: RuleReason,
    /** JSON-serializable facts for logs and the client message (e.g. { have: 3, need: 5 }). */
    readonly details: Record<string, unknown> = {},
  ) {
    super(Object.keys(details).length ? `${reason} ${JSON.stringify(details)}` : reason);
    this.name = 'RuleError';
  }
}

export function isRuleError(e: unknown): e is RuleError {
  return e instanceof RuleError;
}

/** Throws RuleError(reason, details) unless `condition` holds. */
export function check(condition: unknown, reason: RuleReason, details?: Record<string, unknown>): asserts condition {
  if (!condition) throw new RuleError(reason, details);
}
